<script setup lang="ts">
  import { useApiNew } from '@/shared/composables/useApi'
  import { getEntryById } from '@/shared/composables/useEntry'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { useIsHolder } from '@/shared/composables/useIsHolder'
  import { usePerformanceTiming } from '@/shared/composables/usePerformanceTiming'
  import { useScrollToTop } from '@/shared/composables/useScroll'
  import { useVisaPreapproval } from '@/shared/composables/useVisaPreapproval'
  import { useOCRAvailability } from '@/shared/composables/useOCRAvailability'
  import {
    ALLOWED_FLOW_REFERRER_URLS,
    STATE_LIST_INS_KEY,
    viewsMap,
  } from '@/shared/constants/other.constants'
  import { useCoreStore, useFeaturesStore, useVisitorsStore } from '@/shared/store'
  import { PaymentType } from '@/shared/types/response.types'
  import { VisitorType } from '@/shared/types/store.types'
  import { setFormatDate } from '@/shared/utils'
  import { addDays } from 'date-fns'
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'

  import QuoteSection from './components/QuoteSection.vue'
  import VisitorFormSection from './components/VisitorFormSection.vue'
  import { useInboundAddonFlow } from './composables/useInboundAddonFlow'
  import { usePassportExpiryGuard } from './composables/usePassportExpiryGuard'
  import { useQuoteAnalytics } from './composables/useQuoteAnalytics'
  import { useQuotePayment } from './composables/useQuotePayment'
  import { useVisitorEditFlow } from './composables/useVisitorEditFlow'

  interface Emits {
    (e: 'proceed', value?: string): void
    (e: 'load', callback: () => Promise<void>, requestId?: string, withLoader?: boolean): void
    (e: 'error', value: string): void
  }

  const emit = defineEmits<Emits>()

  const homeUrl = useHomeUrl()
  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const featuresStore = useFeaturesStore()
  const router = useRouter()
  const route = useRoute()
  const { locale } = useI18n()
  const { onPerformanceCheckEnd } = usePerformanceTiming()

  const availablePaymentMethods = ref<PaymentType[]>([])
  const isLoading = ref(false)
  const visitors = ref<VisitorType[]>([])
  const termsHref = ref('')
  const benefitsLink = ref('')
  const policyWordingInbound = ref('')

  const getQuote = computed(() => visitorsStore.getQuoteData)
  const getVisitorsPrice = computed(() => visitorsStore.getQuotePrice ?? 0)
  const allVisitors = computed(() => visitorsStore.visitorsList)
  const getEntryValue = computed(() => visitorsStore.getEntryValue)
  const visitDates = computed(() => visitorsStore.getVisitDates)
  const durations = computed(() => coreStore.durations)
  const matchingList = computed(() =>
    visitorsStore.visitorsList.filter((v) => v.durationMatch !== 0)
  )
  const nonMatchingList = computed(() => allVisitors.value.filter((v) => v.durationMatch === 0))
  const visitorsWithDurationMatch = computed(() =>
    visitors.value.filter((v) => v.durationMatch !== 0)
  )
  const getSingleQuotePrice = computed(() =>
    getVisitorsPrice.value ? getVisitorsPrice.value / visitors.value.length : 0
  )

  const formatDate = (val: Date) => setFormatDate(val, locale.value as LocaleType)

  const getTillDate = () => {
    const entry = getEntryById(getEntryValue.value, durations.value)
    if (!entry || typeof entry.duration !== 'number') return '-'
    return formatDate(addDays(visitDates.value.startDate, entry.duration - 1))
  }

  const dates = computed(() => ({
    startDate: formatDate(visitDates.value.startDate),
    tillDate: getTillDate(),
  }))

  const redirectToMainPage = () => {
    window.location.href = homeUrl
  }

  const { getPreapprovalVisitor, hasPreapprovalVisa } = useVisaPreapproval()

  const analytics = useQuoteAnalytics(visitors)

  const {
    isSelected: isInboundAddonSelected,
    totalPriceWithAddon: totalPriceWithInboundAddon,
    inboundPolicyExists,
    isLoading: isLoadingInboundAddon,
    isShown: isShowInboundAddon,
    isAbTest: isInboundAddonAbTest,
    validateBeforePayment: validateInboundOnly,
    onSelectToggle: toggleInboundSelection,
    restoreFromInitialState: restoreInboundFromInitialState,
    checkForHolder: checkInboundForHolder,
    getQuoteForActions: refreshInboundForCrud,
  } = useInboundAddonFlow({
    onToggle: analytics.sendInboundToggle,
    onView: analytics.sendInboundView,
  })

  const getQuotePrice = computed(
    () => totalPriceWithInboundAddon.value ?? visitorsStore.getQuotePrice ?? 0
  )

  const {
    alertText: passportExpiredAlertText,
    isBlocked: isPassportExpiredAndBlocked,
    clear: clearPassportExpiredAlert,
    check: checkPassportExpired,
  } = usePassportExpiryGuard()

  const validateBeforePayment = async (): Promise<boolean> => {
    if (!checkPassportExpired(matchingList.value)) return false

    const result = await validateInboundOnly(visitorsStore.holder)
    if (!result.ok) {
      if (result.error) emit('error', result.error as string)
      return false
    }
    return true
  }

  const onPurchaseStart = ({
    paymentMethod,
    usdPrice,
  }: {
    paymentMethod: number
    usdPrice: string | number
  }) => {
    analytics.sendPurchaseStart(
      getQuote.value.quote || '',
      usdPrice,
      paymentMethod,
      visitors.value.length,
      isInboundAddonSelected.value,
      visitorsStore.inboundPrice || 0
    )
  }

  const { getApplePayTotal, onProceed: onPaymentProceed } = useQuotePayment({
    quoteNumber: computed(() => getQuote.value.quote || ''),
    getQuotePrice,
    nonMatchingListLength: computed(() => nonMatchingList.value.length),
    isLoading,
    validateBeforePayment,
    onPurchaseStart,
    loadEmit: (cb, id, withLoader) => emit('load', cb, id, withLoader),
  })

  const handlePersonDelete = (visitor: VisitorType, currentView: string) =>
    // eslint-disable-next-line camelcase -- GA payload key is snake_case by contract
    analytics.sendDeleteVisitor(visitor, currentView, { btn_title: 'remove' })

  const handlePersonSave = (visitor: VisitorType, isAdd: boolean) =>
    analytics.sendOnSaveVisitor(
      isAdd ? 'added_new_person' : 'click_proceed',
      isAdd ? 'add_person' : 'update_information',
      visitor
    )

  const handlePostSaveInbound = async (visitor: VisitorType) => {
    if (useIsHolder(visitor.relationship)) {
      const result = await checkInboundForHolder(visitor)
      if (!result.ok && !inboundPolicyExists.value) {
        emit('error', result.error as string)
      }
      return
    }
    await refreshInboundForCrud()
  }

  const { isOcrAvailable } = useOCRAvailability()

  const {
    view,
    currentVisitor,
    editVisitorId,
    isHolder,
    isEdit,
    emailIsValid,
    addVisitor,
    editVisitor,
    deleteVisitor,
    saveVisitor,
    restoreFromLocalStorage,
    resetEmailValidity,
  } = useVisitorEditFlow({
    visitors,
    isLoading,
    isSetHolderNationality: isOcrAvailable,
    redirectToMainPage,
    resolvePreapproval: getPreapprovalVisitor,
    hasPreapprovalVisa,
    onAddPerson: analytics.sendAddPerson,
    onEditPerson: analytics.sendEditPerson,
    onDeletePerson: handlePersonDelete,
    onSavePerson: handlePersonSave,
    onViewQuote: analytics.sendViewQuote,
    onBeforeDelete: clearPassportExpiredAlert,
    onPostSaveInbound: handlePostSaveInbound,
    onPostDeleteInbound: refreshInboundForCrud,
    onLoad: (cb, id, withLoader) => emit('load', cb, id, withLoader),
    onProceed: (route) => emit('proceed', route),
  })

  const onInboundAddonSelect = () =>
    emit('load', toggleInboundSelection, 'quote-inbound-addon-select', false)

  const handleOpenRemove = (v: VisitorType) =>
    analytics.sendDialogVisitor(v, view.value, 'delete_person', {})

  const handleCloseRemove = (v: VisitorType) => {
    analytics.sendDialogVisitor(v, view.value, 'click_btn', { btn_title: 'cancel' })
  }

  const checksInfo = async () => {
    await visitorsStore.getTravellers()
    await visitorsStore.getQuote(matchingList.value.length)
    await visitorsStore.updateQuote()
    await coreStore.getDurations()
    await coreStore.loadStoreData()

    await visitorsStore.getQuoteDetails().then((data) => {
      visitors.value = data
      const hasSchemes = Object.prototype.hasOwnProperty.call(getQuote.value, 'schemes')
      if (!hasSchemes || !visitors.value[0]?.firstName) {
        return redirectToMainPage()
      }
      analytics.sendViewQuote()
    })

    availablePaymentMethods.value = await coreStore.getPaymentOptions(String(getQuote.value.quote))

    await restoreInboundFromInitialState()
    restoreFromLocalStorage()
  }

  const fetchTermsAndBenefits = async () => {
    const [terms, benefits] = await Promise.all([
      useApiNew().commonSeervice.getTermsAndConditionsLink(),
      useApiNew().commonSeervice.getBenefitsLink(),
    ])

    termsHref.value = terms
    benefitsLink.value = benefits
  }

  const fetchInboundPolicyWording = async () => {
    if (!isShowInboundAddon.value) return
    policyWordingInbound.value = await useApiNew().commonSeervice.getPolicyWordingInbound()
  }

  const initQuotePage = async () => {
    const referrer = String(route.query.referrer ?? '')
    const isAllowedReferrer = ALLOWED_FLOW_REFERRER_URLS.some((url) => referrer.includes(url))
    if (featuresStore.isFlowDisabled && !isAllowedReferrer) {
      throw new Error('Flow is temporarily disabled')
    }
    await Promise.all([checksInfo(), fetchTermsAndBenefits()])
    await fetchInboundPolicyWording()
    onPerformanceCheckEnd()
  }

  const onBackPressed = () => {
    if (view.value === viewsMap.QUOTE) {
      router.go(-1)
      return
    }
    localStorage.removeItem(STATE_LIST_INS_KEY)
    view.value = viewsMap.QUOTE
  }

  coreStore.setStopBack(true)
  coreStore.setBackCallback(onBackPressed)

  onMounted(() => {
    emit('load', initQuotePage, 'quote-mounted')
  })

  onUnmounted(() => {
    coreStore.setStopBack(false)
    localStorage.removeItem(STATE_LIST_INS_KEY)
  })

  watch(view, (newVal, oldVal) => {
    if (newVal === viewsMap.QUOTE) clearPassportExpiredAlert()
    if (newVal !== oldVal) useScrollToTop()
  })
</script>

<template>
  <div class="quote-widget">
    <QuoteSection
      v-if="view === viewsMap.QUOTE"
      :visitors-with-duration-match="visitorsWithDurationMatch"
      :dates="dates"
      :single-quote-price="getSingleQuotePrice"
      :total-price="getQuotePrice"
      :terms-href="termsHref"
      :benefits-link="benefitsLink"
      :policy-wording-inbound="policyWordingInbound"
      :is-show-inbound-addon="isShowInboundAddon"
      :is-inbound-addon-ab-test="isInboundAddonAbTest"
      :is-inbound-addon-selected="isInboundAddonSelected"
      :is-loading-inbound-addon="isLoadingInboundAddon"
      :inbound-policy-exists="inboundPolicyExists"
      :is-loading="isLoading"
      :is-passport-expired-and-blocked="isPassportExpiredAndBlocked"
      :passport-expired-alert-text="passportExpiredAlertText"
      :available-payment-methods="availablePaymentMethods"
      :get-apple-pay-total-callback="getApplePayTotal"
      :validate-before-payment="validateBeforePayment"
      @add="addVisitor"
      @edit="editVisitor"
      @delete="deleteVisitor"
      @expand="analytics.sendExpand"
      @open-remove-window-event="handleOpenRemove"
      @close-remove-window-event="handleCloseRemove"
      @inbound-toggle="onInboundAddonSelect"
      @on-payment="onPaymentProceed"
      @learn-more="analytics.sendLearnMore"
      @error="emit('error', $event)"
      @close-passport-alert="clearPassportExpiredAlert"
    />

    <VisitorFormSection
      v-if="[viewsMap.ADD_VISITOR, viewsMap.EDIT_VISITOR].includes(view)"
      :visitor="currentVisitor"
      :is-holder="isHolder"
      :is-edit="isEdit"
      :is-loading="isLoading"
      :email-is-valid="emailIsValid"
      :edit-visitor-id="editVisitorId"
      @save="saveVisitor"
      @delete="deleteVisitor"
      @set-email-is-valid="resetEmailValidity"
      @open-remove-window-event="handleOpenRemove"
      @close-remove-window-event="handleCloseRemove"
    />
  </div>
</template>

<style lang="scss" scoped>
  .quote-widget {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-8);
  }
</style>
